import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { catchError, Observable, of, Subject, Subscription } from 'rxjs';
import {
  KpiCardI,
  ClaimRate,
  IncidentType,
  Accommodation,
  AuthService,
  KpiCardComponent,
  ChartsWidget1Component,
  ReportDataService,
  PanelConfigI,
  PanelComponent,
  BarPlotComponent,
  MultipleYAxisChartComponent,
  FilterOption,
  MostCostlyMdcsResponse,
  ChiSquaredResult,
  MdcChartItem,
  MostFrequentMdc,
  MostCostlyICD10sResponse,
  KpiData,
  Incidentclaims,
  ProviderSummary,
} from 'shared-library';
import { DashboardHeaderComponent } from '../provider-peer-comparisons/components/dashboard-header/dashboard-header.component';
import { PanelModule } from 'primeng/panel';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TranslationService } from 'shared-library';
import { FormGroup } from '@angular/forms';
import { DashboardChartDataService } from '../dashboard/services/dashboard-chart-data.service';
import { Select } from "primeng/select";
import { TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeMapChartComponent } from './components/tree-map-chart/tree-map-chart.component';
import { ProviderUtilizationLogicServiceService } from './services/provider-utilization-logic-service.service';
import { TopTenBarChartComponent } from "../../../../../shared-library/src/public-api";

type MonthAgg = 'avg' | 'sum' | 'last';

@Component({
  selector: 'app-provider-utilization',
  templateUrl: './provider-utilization.component.html',
  styleUrls: ['./provider-utilization.component.scss'],
  standalone: true,
  imports: [
    DashboardHeaderComponent,
    ChartsWidget1Component,
    KpiCardComponent,
    PanelModule,
    PanelComponent,
    TranslatePipe,
    BarPlotComponent,
    MultipleYAxisChartComponent,
    Select,
    TitleCasePipe,
    FormsModule,
    TreeMapChartComponent,
    TopTenBarChartComponent
],
  providers: [ProviderUtilizationLogicServiceService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderUtilizationComponent implements OnDestroy {
  private dashboardChartService = inject(DashboardChartDataService);
  translateService = inject(TranslationService);
  private reportData = inject(ReportDataService);
  private logicService = inject(ProviderUtilizationLogicServiceService);
  rows: any;
  temp: any[] = [];
  tableData: any;
  loadingIndicator = true;
  reorderable = true;

  showStatistics: boolean = false;

  kpiCardsConfig: WritableSignal<KpiCardI[]> = signal([
    { icon: 'fas fa-file-alt', data: 0, label: 'TOTAL_CLAIMS' },
    { icon: 'fas fa-hourglass-half', data: 0, label: 'PENDING_CLAIMS' },
    { icon: 'fas fa-check', data: 0, label: 'APPROVED_CLAIMS' },
    { icon: 'fa-solid fa-x', data: 0, label: 'REJECTED_CLAIMS' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'PAID_CLAIMS' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'TOTAL_CLAIM_AMOUNT' },
  ]);
  moreKpiCardsConfig: WritableSignal<KpiCardI[]> = signal([
    { icon: 'fas fa-euro-sign', data: 0, label: 'TOTAL_DISCOUNT_AMOUNT' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'PUBLIC_INSURANCE_AMOUNT' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'OTHER_INSURANCE_COVERAGE_AMOUNT' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'OUT_OF_POCKET' },
    { icon: 'fas fa-hourglass-half', data: 0, label: 'PENDING_CLAIM_AMOUNT' },
    { icon: 'fa-solid fa-x', data: 0, label: 'REJECTED_CLAIM_AMOUNT' },
    { icon: 'fas fa-users', data: 0, label: 'NUMBER_OF_CLAIMANTS' },
    { icon: 'fas fa-user-friends', data: 0, label: 'CLAIMS_PER_INSURED' },
    { icon: 'fas fa-clock', data: 0, label: 'AVERAGE_HOSPITALIZATION_DURATION', unit: 'Days' },
    { icon: 'fas fa-euro-sign', data: 0, label: 'CHARGED_AMOUNT_PER_CLAIM' },
    { icon: 'fas fa-calculator', data: 0, label: 'CLAIMS_FREQUENCY' },
    { icon: 'fas fa-clock', data: 0, label: 'AVERAGE_CLAIM_LIFETIME' },
  ]);

  kpiPanelConfig: PanelConfigI = {
    toggleable: true,
    collapsed: true,
    expandIcon: undefined,
    collapseIcon: undefined,
    headerIcon: 'fa-solid fa-chart-simple',
    headerLabel: 'KPIs',
  };

  claimRate = signal<ClaimRate[]>([]);
  incidentType = signal<IncidentType[]>([]);
  expensesCategories = signal<{ category: string; grossAmount: number }[]>([]);
  accommondationType = signal<Accommodation[]>([]);
  topTenDrg = signal<{ title: string; amount: number; codeDesc: string; }[]>([]);;

  private subscriptions: Subscription[] = [];
  dataLoaded = signal<boolean>(false);

  private expensesCategoriesRaw: { category: string; grossAmount: number }[] = [];

  form!: FormGroup;
  chartFilters = signal<FilterOption[][]>([]);

  incidentTypeTooltip = signal<
    (label: any, seriesIndex: number) => string
  >(() => '');

  mostCostlyICD10sChartData = signal<any[]>([]);
  mostCostlyICD10sChartHoverLabels = signal<any[]>([]);
  mostCostlyICD10sChartFormatTooltip = signal<
    (label: any, seriesIndex: number) => string
  >(() => '');
  mostCostlyICD10sSelectedFilters = signal<Record<string, string>>({});
  mostCostlyICD10sStats?: string = '';

  mostCostlyMdcsChartData = signal<any[]>([]);
  mostCostlyMdcsChartHoverLabels = signal<any[]>([]);
  mostCostlyMdcsChartFormatTooltip = signal<(label: any, seriesIndex: number) => string>(() => '');
  mostCostlyMdcsSelectedFilters = signal<Record<string, string>>({});
  mostCostlyMdcsStats?: string = '';

  mostFrequentMdcSignal: WritableSignal<MdcChartItem[]> = signal([]);
  mostFrequentMdcChiSquaredResult: WritableSignal<ChiSquaredResult> = signal(
    {} as ChiSquaredResult
  );
  mostFrequentMdcSelectedFilters = signal<Record<string, string>>({});

  globalSelectedFilters = signal<Record<string, string>>({
    contractType: 'Ατομικά',
  });

  compareBy4 = signal<string>('clinic');
  compareByMostCostlyMdc = signal<string>('clinic');
  compareByFrequentMdc = signal<string>('clinic');

  chartReadyCostlyMdc = signal(false);
  chartReadyIcd = signal(false);
  chartReadyKen = signal(false);
  chartReadyFrequentMdc = signal(false);

  private initialFetchDone = false;

  constructor(
    private translate: TranslateService
  ) {
    this.initializeFilters();
  }

  private initializeFilters(): void {
    const reportsDatesSub = this.dashboardChartService
      .getDashboardYears()
      .pipe(
        catchError((error) => {
          console.error('Error fetching dashboard years', error);
          return of([]);
        })
      )
      .subscribe((years) => {
        const yearOptions = this.logicService.buildYearOptions(years ?? []);
        this.chartFilters.set(this.logicService.buildFilters(yearOptions));

        if (!this.globalSelectedFilters()['years']) {
          const availableYears = yearOptions.filter((option) => option.value !== 'All');

          if (availableYears.length > 0) {
            const currentYear = String(new Date().getFullYear());
            const matchedYear =
              availableYears.find((option) => String(option.value) === currentYear) ??
              availableYears[0];

            this.globalSelectedFilters.update((current) => ({
              ...current,
              years: String(matchedYear.value),
            }));
          }
        }

        if (!this.initialFetchDone) {
          this.initialFetchDone = true;
          this.fetchData();
        }
      });

    this.subscriptions.push(reportsDatesSub);
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }


  onFilterSelect(key: string, value: string | number) {
    const selectedValue = value == null || value === '' ? 'All' : String(value);
    this.globalSelectedFilters.set({
      ...this.globalSelectedFilters(),
      [key]: selectedValue
    });

    if (key === 'years' && (value === 'All' || !value)) {
    this.globalSelectedFilters.update(current => ({
      ...current,
      ['quarter']: 'All'
    }));
    }

    this.fetchData();
  }

  onGlobalFilterChange(updatedFilters: Record<string, string>) {
    this.globalSelectedFilters.set({
      ...this.globalSelectedFilters(),
      ...updatedFilters
    });

    this.fetchData();
  }

  private fetchData() {
    const filters = this.globalSelectedFilters();
    const year = filters['years'] || 'All';
    const contractType = filters['contractType'] || 'All';
    const quarter = filters['quarter'] || 'All';
    // KPI Data
    const kpiSub = this.reportData.getKpiData(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching KPI Data', error);
        return of([]);
      })
    ).subscribe((kpiDataResult) => {
      this.handleKpiDataResult(kpiDataResult);
    });

    // Claim Rate
    const claimRateSub = this.reportData.getClaimDatesRate(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching Claim Rate Data', error);
        return of([]);
      })
    ).subscribe((claimRateResult: ClaimRate[]) => {
      if (claimRateResult) {
        const daily = claimRateResult
          .filter((r) => r?.title)
          .sort(
            (a, b) =>
              new Date(String(a.title).replace(/\//g, '-')).getTime() -
              new Date(String(b.title).replace(/\//g, '-')).getTime()
          );
        this.claimRate.set(this.groupClaimRateByMonth(daily, 'avg'));
      }
    });

    // Incident Type
    const incidentTypeSub = this.reportData.getIncidentClaims(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching Incident Data', error);
        return of([]);
      })
    ).subscribe((incidentTypeResult: Incidentclaims[]) => {
      if (incidentTypeResult) {
        let data = incidentTypeResult.map((m) => ({
          title: m.incident,
          amount: m.claimVolumePercentage,
          desc: m.claimVolume
        }));

        this.incidentType.set(data);
      }
    });

    // Expenses Categories
    const expensesCategoriesSub = this.reportData.getMedicalExpensesDistribution(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching Expenses Categories Data', error);
        return of([]);
      })
    ).subscribe((expensesCategoriesResult) => {
      if (expensesCategoriesResult) {
        this.expensesCategoriesRaw = expensesCategoriesResult;
        this.expensesCategories.set(this.expensesCategoriesRaw)
      }
    });

    // Accommodation Type
    const accommodationSub = this.reportData.getAccommodationClaims(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching Accommodation Data', error);
        return of([]);
      })
    ).subscribe((accommodationTypeResult) => {
      if (accommodationTypeResult) {
        this.handleAccommodationTypeResult(accommodationTypeResult);
      }
    });

    // Top 10 DRG
    const topTenDrgSub = this.reportData.getDRGSummaryProvider(year, contractType,quarter).pipe(
      catchError((error) => {
        console.error('Error fetching Top 10 DRG Data', error);
        return of([]);
      })
    ).subscribe((topTenDrgResult) => {
      this.handleTopTenKenResult(topTenDrgResult)
    });

    // Most Costly ICD10s
    const mostCostlyICD10sSub = this.getMostCostlyICD10sData().subscribe((mostCostlyICD10sResult: MostCostlyICD10sResponse) => {
      if (mostCostlyICD10sResult) {
        this.handleMostCostlyICD10sResult(mostCostlyICD10sResult);
      }
    });

    // Most Costly MDCs
    const mostCostlyMdcsSub = this.getMostCostlyMdcsData().subscribe((mostCostlyMdcsResult: MostCostlyMdcsResponse) => {
      if (mostCostlyMdcsResult) {
        this.handleMostCostlyMdcsResult(mostCostlyMdcsResult);
      }
    });

    // Most Frequent MDC
    const mostFrequentMdcSub = this.getMostFrequentMdc().pipe(
      catchError(() =>
        of({
          filters: {},
          chartData: [],
        } as unknown as MostFrequentMdc)
      )
    ).subscribe((mostFrequentMdc) => {
      if (mostFrequentMdc) {
        this.handleMostFrequentMdc(mostFrequentMdc);
      }
    });

    this.subscriptions.push(
      kpiSub,
      claimRateSub,
      incidentTypeSub,
      expensesCategoriesSub,
      accommodationSub,
      topTenDrgSub,
      mostCostlyICD10sSub,
      mostCostlyMdcsSub,
      mostFrequentMdcSub
    );
  }

  toggleStatistics() {
    this.showStatistics = !this.showStatistics;
  }

  timeout: any;
  onPage(event: any) {
    clearTimeout(this.timeout);
  }

  private avg = (total: number, count: number) =>
    +(((+total || 0) / Math.max((+count || 0), 1)).toFixed(2));

  onMostCostlyICD10sFilterChanged(updatedFilter: Record<string, string>) {
    this.mostCostlyICD10sSelectedFilters.update((current: any) => ({
      ...current,
      ...updatedFilter,
    }));
    this.getMostCostlyICD10sData().subscribe((result: MostCostlyICD10sResponse) => {
      this.handleMostCostlyICD10sResult(result);
    });
  }

  onMostCostlyMdcsFilterChanged(updatedFilter: Record<string, string>) {
    this.mostCostlyMdcsSelectedFilters.update((current: any) => ({
      ...current,
      ...updatedFilter,
    }));
    this.getMostCostlyMdcsData().subscribe((result: MostCostlyMdcsResponse) => {
      this.handleMostCostlyMdcsResult(result);
    });
  }

  onMostFrequentMdcFiltersChange(updatedFilter: Record<string, string>) {
    this.mostFrequentMdcSelectedFilters.update((current: any) => ({
      ...current,
      ...updatedFilter,
    }));
    this.getMostFrequentMdc().subscribe((result) => {
      this.handleMostFrequentMdc(result);
    });
  }



  mdcTooltip = (item: any, seriesIndex: number, totalSeries: number) => {
    const cumulativeIndex = totalSeries - 1;

    switch (seriesIndex) {
      // current data
      case 0:
        return `
        <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
          <strong>${this.translate.instant('MDC.TOOLTIP.CODE')}:</strong> ${item.mdcCode}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.DESCRIPTION')}:</strong> ${item.mdcDesc}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.COUNT')}:</strong> ${item.count}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.PERCENT')}:</strong> ${item.currentPercent.toFixed(2)}%
        </div>
      `;
      // historical or cumulative when seriesIndex is 1
      case 1:
        if (seriesIndex === cumulativeIndex) {
          return `
          <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
            <strong>${this.translate.instant('MDC.TOOLTIP.CUMULATIVE_PERCENT')}:</strong> ${item.cumulativeCurrentPercent.toFixed(2)}%
          </div>
        `;
        }
        // historical
        return `
        <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
          <strong>${this.translate.instant('MDC.TOOLTIP.CODE')}:</strong> ${item.mdcCode}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.DESCRIPTION')}:</strong> ${item.mdcDesc}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.COUNT')}:</strong> ${item.historicalCount}<br/>
          <strong>${this.translate.instant('MDC.TOOLTIP.PERCENT')}:</strong> ${item.historicalPercent.toFixed(2)}%
        </div>
      `;
      // cumulative with historical
      case 2:
        return `
        <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
          <strong>${this.translate.instant('MDC.TOOLTIP.CUMULATIVE_PERCENT')}:</strong> ${item.cumulativeCurrentPercent.toFixed(2)}%
        </div>
      `;
      default:
        return '';
    }
  };

  onKenFilterChange(event: boolean | string) {
    const filters = this.globalSelectedFilters();
    const year = filters['years'] || 'All';
    const contractType = filters['contractType'] || 'All';
    const quarter = filters['quarter'] || 'All';

    this.topTenDrg.set([]);

    const isKenBoolean = typeof event === "boolean" ? event : false;

    this.reportData.getDRGSummaryProvider(year, contractType, quarter, isKenBoolean)
      .subscribe({
        next: (value) => this.handleTopTenKenResult(value)
      });
  }

  private getMostCostlyICD10sData() {
    const filters = this.globalSelectedFilters();

    const year = filters['years'] || 'All';
    const month = 'All';
    const compareBy = 'clinic';
    const topNMostCostly = 10;
    const groups = 'All';
    const contractType = filters['contractType'] || 'All';
    const quarter = filters['quarter'] || "All";

    return this.dashboardChartService
      .getMostCostlyICD10s(year, month, compareBy, groups, +topNMostCostly, contractType, quarter)
      .pipe(
        catchError((error) => {
          console.error('Error fetching Avg Claim Amount ICD10s', error);
          return of({
            chartFilters: {
              icd10CodeTitles: [],
              providers: [],
              clinics: [],
              providerGroups: [],
              years: [],
              months: [],
            },
            chartData: [],
          });
        })
      );
  }

  private getMostCostlyMdcsData() {
    const filters = this.globalSelectedFilters();

    const year = filters['years'] || 'All';
    const month = 'All';
    const compareBy = 'clinic';
    let groups = 'All';
    const topNMostCostly = 10;
    const contractType = filters['contractType'] || 'All';
    const quarter = filters['quarter'] || "All";

    return this.dashboardChartService
      .getMostCostlyMdcs(year, month, compareBy, groups, +topNMostCostly, contractType, quarter)
      .pipe(
        catchError((error) => {
          console.error('Error fetching Most Costly Mdcs', error);
          return of({
            chartFilters: {
              mdcCodeTitles: [],
              providers: [],
              clinics: [],
              providerGroups: [],
              years: [],
              months: [],
            },
            chartData: [],
          });
        })
      );
  }

  private getMostFrequentMdc(): Observable<MostFrequentMdc> {
    const filters = this.globalSelectedFilters();
    const year = filters['years'] || 'All';
    const month = 'All';
    const compareBy = 'clinic';
    let groups = 'All';
    const contractType = filters['contractType'] || 'All';

    const topN = 10;
    const quarter = filters['quarter'] || "All";
    return this.dashboardChartService
      .getMostFrequentMdc(year, month, compareBy, groups, +topN, contractType, quarter)
      .pipe(
        catchError((error) => {
          return of({
            filters: {
              years: [],
              providers: [],
              month: [],
              clinics: [],
              ProvidersGroup: [],
            },
            chartData: [],
          } as MostFrequentMdc);
        })
      );
  }


  private handleMostCostlyICD10sResult(
    mostCostlyICD10sResult: MostCostlyICD10sResponse
  ) {
    //handle chart data
    let chartData: any[] = [];

    for (const [i, item] of mostCostlyICD10sResult.chartData.entries()) {
      chartData.push({
        title: item.code,
        description: item.title,
        currentCount: item.focusCount,
        currentPerc: item.focusPercentage,
        currentDisplayData: item.focusTotalCost,
        historicalCount: item.historicalAvgCount,
        historicalPerc: item.historicalAvgPercentage,
        historicalDisplayData: item.historicalAvgTotalCost,
      });
    }
    chartData = chartData.sort((a, b) => b.currentDisplayData - a.currentDisplayData)

    this.mostCostlyICD10sChartData.set([...chartData]);

    //handle stat result text
    this.mostCostlyICD10sStats = `<strong>Mann-Whitney U Test Result</strong>
    ${mostCostlyICD10sResult.chartData
        .map((item) => {
          return `
        <div class="mb-2">
          <strong>${item.code} (${item.title}):</strong>
          u-value:</strong> ${item.mannWhitneyU.toFixed(2)}
          p-value:</strong> ${Math.min(0.99, Math.trunc(Number(item.mannWhitneyPValue) * 100) / 100).toFixed(2)}
        </div>
      `;
        })
        .join('')}`;

    //handle hover data
    const hoverLabels = mostCostlyICD10sResult.chartData.map((item) => ({
      title: item.code,
      current: {
        description: item.title,
        count: item.focusCount,
        percentage: item.focusPercentage,
        avg: this.avg(item.focusTotalCost, item.focusCount),
      },
      historical: {
        description: item.title,
        count: item.historicalAvgCount,
        percentage: item.historicalAvgPercentage,
        avg: this.avg(item.historicalAvgTotalCost, item.historicalAvgCount),
      },
    }));
    this.mostCostlyICD10sChartHoverLabels.set([...hoverLabels]);

    // handle tooltip
    const formatTooltip = (label: any, seriesIndex: number): string => {
      const t = this.translate.instant.bind(this.translate);
      const group = seriesIndex === 0 ? label.current : label.historical;

      return `
    <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
      <strong>${label.title}</strong><br/>
      ${t('ICD10.TOOLTIP.DESCRIPTION')}: ${group.description}<br/>
      ${t('ICD10.TOOLTIP.COUNT')}: ${group.count}<br/>
      ${t('ICD10.TOOLTIP.PERCENTAGE')}: ${group.percentage}%<br/>
      ${t('ICD10.TOOLTIP.AVG_COST') || 'Average Cost'}: €${group.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </div>
  `;
    };

    this.mostCostlyICD10sChartFormatTooltip.set(formatTooltip);
    this.chartReadyIcd.set(true);

  }

  private handleMostCostlyMdcsResult(mostCostlyMdcsResult: MostCostlyMdcsResponse) {
    let chartData: any[] = [];

    for (const [i, item] of mostCostlyMdcsResult.chartData.entries()) {
      chartData.push({
        title: item.code,
        description: item.title,
        currentCount: item.focusCount,
        currentPerc: item.focusPercentage,
        currentDisplayData: item.focusTotalCost,
        historicalCount: item.historicalAvgCount,
        historicalPerc: item.historicalAvgPercentage,
        historicalDisplayData: item.historicalAvgTotalCost,
      });
    }

    this.mostCostlyMdcsChartData.set([...chartData]);

    //handle stat result text
    this.mostCostlyMdcsStats = `<strong>Mann-Whitney U Test Result</strong>
    ${mostCostlyMdcsResult.chartData
        .map((item) => {
          return `
        <div class="mb-2">
          <strong>${item.code} (${item.title}):</strong>
          u-value:</strong> ${item.mannWhitneyU.toFixed(2)}
          p-value:</strong> ${Math.min(0.99, Math.trunc(Number(item.mannWhitneyPValue) * 100) / 100).toFixed(2)}
        </div>
      `;
        })
        .join('')}`;

    //handle hover data
    const hoverLabels = mostCostlyMdcsResult.chartData.map((item) => ({
      title: item.code,
      current: {
        description: item.title,
        count: item.focusCount,
        percentage: item.focusPercentage,
        total: item.focusTotalCost,
      },
      historical: {
        description: item.title,
        count: item.historicalAvgCount,
        percentage: item.historicalAvgPercentage,
        total: item.historicalAvgTotalCost,
      },
    }));
    this.mostCostlyMdcsChartHoverLabels.set([...hoverLabels]);

    // handle tooltip
    const formatTooltip = (label: any, seriesIndex: number): string => {
      const t = this.translate.instant.bind(this.translate); // shorthand
      const group = seriesIndex === 0 ? label.current : label.historical;

      return `
    <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
      <strong>${label.title}</strong><br/>
      ${t('MDC.TOOLTIP.DESCRIPTION')}: ${group.description}<br/>
      ${t('MDC.TOOLTIP.COUNT')}: ${group.count}<br/>
      ${t('MDC.TOOLTIP.PERCENTAGE')}: ${group.percentage}%<br/>
      ${t('MDC.TOOLTIP.TOTAL_COST')}: €${group.total}
    </div>
  `;
    };

    this.mostCostlyMdcsChartFormatTooltip.set(formatTooltip);
    this.chartReadyCostlyMdc.set(true);

  }

  private handleMostFrequentMdc(claimData: MostFrequentMdc) {
    this.mostFrequentMdcChiSquaredResult.set(claimData.chiSquaredResult!);

    const chartData = claimData.chartData;
    this.mostFrequentMdcSignal.set(chartData);
    this.chartReadyFrequentMdc.set(true)
  }

  private handleTopTenKenResult(topTenDrgResult: ProviderSummary[]) {
    if (topTenDrgResult) {
      topTenDrgResult.sort((a, b) => b.totalHospitalCharge - a.totalHospitalCharge);
      topTenDrgResult = topTenDrgResult.slice(0, 10);

      this.topTenDrg.set(topTenDrgResult.map((m) => ({
        title: m.kenCode,
        amount: m.totalHospitalCharge,
        codeDesc: m.ken
      })));
    }
    this.chartReadyKen.set(true);
  }

  private handleAccommodationTypeResult(
    accommodationTypeResult: Accommodation[]
  ) {
    const titleMapping: Record<string, string> = {
      "Intensive Care Unit (ICU)": "ΜΕΘ",
      "C Class": "Τρίκλινο",
      "A Class": "Μονόκλινο",
    };

    let bClassAmount = 0;
    let bClassCount = 0;
    let luxAmount = 0;
    let luxCount = 0;

    const newAccommodationList = accommodationTypeResult.reduce<Accommodation[]>(
      (acc, item) => {
        if (["B1 Class", "B2 Class"].includes(item.title)) {
          bClassAmount += item.amount;
          bClassCount += item.count;
        } else if (["Luxury", "Suite"].includes(item.title)) {
          luxAmount += item.amount;
          luxCount += item.count;
        } else if (titleMapping[item.title]) {
          acc.push({ ...item, title: titleMapping[item.title] });
        } else {
          acc.push(item);
        }
        return acc;
      },
      []
    );

    if (bClassAmount > 0) {
      newAccommodationList.push({ title: "Δίκλινο", amount: bClassAmount, count: bClassCount });
    }

    if (luxAmount > 0) {
      newAccommodationList.push({ title: "Lux", amount: luxAmount, count: luxCount });
    }

    newAccommodationList.sort((a, b) => b.amount - a.amount);

    const data = newAccommodationList.map((m) => ({
      title: m.title,
      amount: m.amount,
      desc: m.count
    }));

    this.accommondationType.set(data as any);
  }

  private handleKpiDataResult(kpiDataResult: KpiData[]) {

    this.kpiCardsConfig.update((currentKpiCards) => {
      const newKpiCards = [...currentKpiCards];
      kpiDataResult.forEach((kpi) => {
        const kpiCard = newKpiCards.find((card) => card.label === kpi.name);
        if (kpiCard) {
          kpiCard.data = kpi.value;
        }
      });
      return newKpiCards;
    });

    this.moreKpiCardsConfig.update((currentMoreKpiCards) => {
      const newMoreKpiCards = [...currentMoreKpiCards];
      kpiDataResult.forEach((kpi) => {
        const moreKpi = newMoreKpiCards.find((card) => card.label === kpi.name);
        if (moreKpi) {
          moreKpi.data = kpi.value;
        }
      });
      return newMoreKpiCards;
    });

  }

  // helpers
  private monthKey(dateLike: string): string {
    const d = new Date(String(dateLike).replace(/\//g, '-'));
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${y}-${m}`;
  }

  private formatMonthLabel(key: string): string {
    const [y, m] = key.split('-');
    return `${parseInt(m, 10)}/${y}`;
  }

  private groupClaimRateByMonth(
    data: Array<{ title: string; amount: number }>,
    agg: MonthAgg = 'avg'
  ): Array<{ title: string; amount: number }> {
    const buckets = new Map<string, { total: number; count: number; last: number }>();

    for (const r of data) {
      const k = this.monthKey(r.title);
      const entry = buckets.get(k) ?? { total: 0, count: 0, last: r.amount };
      entry.total += Number(r.amount) || 0;
      entry.count += 1;
      entry.last = r.amount;
      buckets.set(k, entry);
    }

    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({
        title: this.formatMonthLabel(k),
        amount:
          agg === 'sum'
            ? +v.total.toFixed(2)
            : agg === 'last'
              ? +v.last.toFixed(2)
              : +((v.total / Math.max(1, v.count))).toFixed(2),
      }));
  }

}
